import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-3 — NovaContaModal: cria conta bancária OU edita existente.
// Suporta modo criar (account=null) e editar (account=obj).

const ACCOUNT_TYPES = [
  { value: "checking", label: "Conta corrente" },
  { value: "savings",  label: "Poupança" },
  { value: "cash",     label: "Dinheiro" },
  { value: "other",    label: "Outro" },
];

function _toCents(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function _formatBRL(v) {
  if (v == null) return "";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ValidationMsg({ children }) {
  return <p className="text-micro text-status-error mt-1" role="alert">{children}</p>;
}

export default function NovaContaModal({ open, onOpenChange, account, onSubmit }) {
  const isEditing = Boolean(account);
  const [name, setName] = useState("");
  const [type, setType] = useState("checking");
  const [institution, setInstitution] = useState("");
  const [balance, setBalance] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setName(account?.name || "");
      setType(account?.type || "checking");
      setInstitution(account?.institution || "");
      setBalance(_formatBRL(account?.current_balance));
      setSaving(false);
      setTouched({});
    }
  }, [open, account]);

  const errors = {};
  const nameTrim = (name || "").trim();
  if (!nameTrim) errors.name = "Informe um nome";
  else if (nameTrim.length > 50) errors.name = "Máx 50 caracteres";
  const bal = _toCents(balance);
  if (balance !== "" && bal == null) errors.balance = "Valor inválido";

  const hasErrors = Object.keys(errors).length > 0;
  const showErr = (k) => touched[k] && errors[k];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, balance: true });
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = {
        name: nameTrim,
        type,
        institution: institution.trim() || null,
        current_balance: _toCents(balance) ?? 0,
      };
      // user_id legacy ignorado pelo backend (SEC-1) mas POST exige no shape antigo
      if (!isEditing) payload.user_id = "ignored";
      await onSubmit(payload, account?.id);
      toast.success(isEditing ? "Conta atualizada" : "Conta criada");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-md"
        data-testid="contas-nova-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="contas-nova-modal-title">
            {isEditing ? "Editar conta bancária" : "Nova conta bancária"}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            {isEditing
              ? "Atualize as informações da conta."
              : "Cadastre uma conta corrente, poupança ou dinheiro em espécie."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 py-2">
            <div>
              <label htmlFor="nc-name" className="block text-caption font-medium text-foreground-muted mb-1">
                Nome
              </label>
              <Input
                id="nc-name"
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, name: true }))}
                placeholder="Ex: Itaú Corrente, Poupança Caixa"
                maxLength={60}
                aria-invalid={Boolean(showErr("name"))}
                data-testid="contas-nova-modal-name"
              />
              {showErr("name") && <ValidationMsg>{errors.name}</ValidationMsg>}
            </div>

            <div>
              <label className="block text-caption font-medium text-foreground-muted mb-1.5">
                Tipo
              </label>
              <div className="flex flex-wrap gap-2">
                {ACCOUNT_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-caption font-medium border transition-colors",
                      type === t.value
                        ? "bg-brand-cyan/15 border-brand-cyan/40 text-brand-cyan"
                        : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
                    )}
                    data-testid={`contas-nova-modal-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="nc-inst" className="block text-caption font-medium text-foreground-muted mb-1">
                Instituição (opcional)
              </label>
              <Input
                id="nc-inst"
                type="text"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="Ex: Itaú, Caixa, C6 Bank"
                maxLength={60}
                data-testid="contas-nova-modal-institution"
              />
            </div>

            <div>
              <label htmlFor="nc-bal" className="block text-caption font-medium text-foreground-muted mb-1">
                Saldo atual (R$)
              </label>
              <Input
                id="nc-bal"
                type="text"
                inputMode="decimal"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, balance: true }))}
                placeholder="0,00"
                aria-invalid={Boolean(showErr("balance"))}
                data-testid="contas-nova-modal-balance"
              />
              {showErr("balance") && <ValidationMsg>{errors.balance}</ValidationMsg>}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="contas-nova-modal-cancel">
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={saving || hasErrors} data-testid="contas-nova-modal-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing ? "Salvar" : "Criar conta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
